import { useMemo, useCallback, useState } from "react";
import { useInvalidate, useTranslate, CanAccess } from "@refinedev/core";
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Flex,
  TableColumnsType,
  Card,
  Input,
  Badge,
} from "antd";
import { DeleteButton, DateField } from "@refinedev/antd";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";
import { useWorkspace } from "hooks/useWorkspace";
import { addDateTz } from "pages/media/utils";
import { UserInviteResponse } from "types/";
import { MiniLabel } from "components/MiniLabel/";
import dayjs from "dayjs";
import { Empty } from "components/Empty/";
import { UsersIcon, PaperPlaneTiltIcon } from "@phosphor-icons/react";
import { useUserRoles } from "hooks/useUserRoles";

const createDateSorter = <T extends Record<string, unknown>>(
  field: keyof T
) => {
  return (a: T, b: T) => {
    const dateA = a[field];
    const dateB = b[field];

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    if (typeof dateA !== "string" || typeof dateB !== "string") {
      return 0;
    }
    return dayjs(dateA).diff(dayjs(dateB));
  };
};

const createdSorter = createDateSorter<UserInviteResponse>("created");
const expirySorter = createDateSorter<UserInviteResponse>("expiry");

const getInviteStatus = (
  record: UserInviteResponse
): "accepted" | "expired" | "pending" => {
  if (record.user_id) return "accepted";
  if (record.expiry && dayjs(record.expiry).isBefore(dayjs())) return "expired";
  return "pending";
};

const STATUS_BADGE_MAP: Record<
  ReturnType<typeof getInviteStatus>,
  "success" | "error" | "processing"
> = {
  accepted: "success",
  expired: "error",
  pending: "processing",
};

interface UserInvitesTableProps {
  organizationId: string;
  invitesData: UserInviteResponse[] | undefined;
  isLoading: boolean;
  onEdit: (record: UserInviteResponse) => void;
  onSendFirst?: () => void;
}

export const UserInvitesTable = ({
  organizationId,
  invitesData,
  isLoading,
  onEdit,
  onSendFirst,
}: UserInvitesTableProps) => {
  const t = useTranslate();
  const invalidate = useInvalidate();
  const { workspaces } = useWorkspace({
    organizationId: organizationId,
  });
  const [searchText, setSearchText] = useState("");

  const { organizationRoleOptions, workspaceRoleOptions } = useUserRoles();

  const getRoleLabel = useMemo(() => {
    return (roleValue: string, isWorkspaceRole = false) => {
      const roleArray = isWorkspaceRole
        ? workspaceRoleOptions
        : organizationRoleOptions;
      const roleObj = roleArray.find((role) => role.value === roleValue);
      return roleObj ? roleObj.label : roleValue;
    };
  }, [organizationRoleOptions, workspaceRoleOptions]);

  const filteredData = useMemo(() => {
    if (!invitesData) return [];
    if (!searchText.trim()) return invitesData;
    const lowerSearch = searchText.toLowerCase();
    return invitesData.filter((invite) =>
      invite.recipient.toLowerCase().includes(lowerSearch)
    );
  }, [invitesData, searchText]);

  const onDeleteSuccess = useCallback(() => {
    void invalidate({
      resource: `api/${organizationId}/user-invites`,
      invalidates: ["list"],
    });
  }, [organizationId, invalidate]);

  const statusTranslationMap: Record<
    ReturnType<typeof getInviteStatus>,
    string
  > = useMemo(
    () => ({
      pending: t("pages.userInvites.statusPending"),
      expired: t("pages.userInvites.statusExpired"),
      accepted: t("pages.userInvites.statusAccepted"),
    }),
    [t]
  );

  const columns: TableColumnsType<UserInviteResponse> = useMemo(
    () => [
      {
        title: t("pages.login.fields.email"),
        dataIndex: "recipient",
        key: "recipient",
        render: (recipient: string, record: UserInviteResponse) => {
          const status = getInviteStatus(record);
          return (
            <Flex align="center" gap={8}>
              <Typography.Text>{recipient}</Typography.Text>
              <Badge
                status={STATUS_BADGE_MAP[status]}
                text={statusTranslationMap[status]}
              />
            </Flex>
          );
        },
      },
      {
        title: t("pages.userInvites.roles"),
        dataIndex: "organization_roles",
        key: "organization_roles",
        width: 400,
        render: (roles: string[], record) => (
          <Flex vertical gap={10}>
            {roles?.length > 0 && (
              <Flex vertical>
                <MiniLabel>
                  {t("pages.userInvites.organizationRoles")}
                </MiniLabel>
                <Flex wrap gap={4}>
                  {roles.map((role) => (
                    <Tag key={role} color="blue">
                      {getRoleLabel(role)}
                    </Tag>
                  ))}
                </Flex>
              </Flex>
            )}
            {record.workspace_roles &&
              Object.keys(record.workspace_roles).length > 0 && (
                <Flex vertical>
                  <MiniLabel>{t("pages.userInvites.workspaceRoles")}</MiniLabel>
                  <Flex wrap gap={4}>
                    {Object.entries(record.workspace_roles).map(
                      ([workspaceId, workspaceRoles]) => {
                        const workspace = workspaces?.find(
                          (w) => w.id === workspaceId
                        );

                        return workspaceRoles.map((role) => (
                          <Tag key={`${workspaceId}-${role}`} color="cyan">
                            {workspace
                              ? `${workspace.name}: ${getRoleLabel(role, true)}`
                              : getRoleLabel(role, true)}
                          </Tag>
                        ));
                      }
                    )}
                  </Flex>
                </Flex>
              )}
          </Flex>
        ),
      },
      {
        title: t("posts.fields.createdAt"),
        dataIndex: "created",
        key: "created",
        sorter: createdSorter,
        defaultSortOrder: "descend",
        render: (created: string) =>
          created ? (
            <DateField format="LLL" value={addDateTz(created)} />
          ) : null,
      },
      {
        title: t("pages.userInvites.expiry"),
        dataIndex: "expiry",
        key: "expiry",
        sorter: expirySorter,
        render: (expiry: string) =>
          expiry ? (
            <DateField format="LLL" value={addDateTz(expiry)} />
          ) : (
            <Typography.Text type="secondary">
              {t("pages.userInvites.noExpiry")}
            </Typography.Text>
          ),
      },
      {
        title: t("table.actions"),
        key: "actions",
        render: (_, record) => (
          <Space>
            <CanAccess resource="user_invite" action="edit">
              <Button
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
                size="small"
                type="default"
              >
                {t("buttons.edit")}
              </Button>
            </CanAccess>
            <CanAccess resource="user_invite" action="delete">
              <DeleteButton
                size="small"
                recordItemId={record.id}
                resource={`api/user-invites`}
                accessControl={{ enabled: false }}
                onSuccess={onDeleteSuccess}
                confirmTitle={t("pages.userInvites.deleteInvitation")}
                confirmOkText={t("buttons.delete")}
                confirmCancelText={t("buttons.cancel")}
              />
            </CanAccess>
          </Space>
        ),
      },
    ],
    [t, getRoleLabel, workspaces, onEdit, onDeleteSuccess, statusTranslationMap]
  );

  const hasData = invitesData && invitesData.length > 0;

  const emptyState = (
    <Empty
      image={<UsersIcon size={100} aria-hidden="true" />}
      description={
        <Flex vertical align="center" gap={8}>
          <Typography.Text strong>
            {t("pages.userInvites.emptyTitle")}
          </Typography.Text>
          <Typography.Text type="secondary">
            {t("pages.userInvites.emptyDescription")}
          </Typography.Text>
          {onSendFirst && (
            <Button
              type="primary"
              icon={<PaperPlaneTiltIcon size={16} aria-hidden="true" />}
              onClick={onSendFirst}
              style={{ marginTop: 8 }}
              shape="round"
            >
              {t("pages.userInvites.sendFirstInvitation")}
            </Button>
          )}
        </Flex>
      }
    />
  );

  return (
    <Card styles={{ body: { padding: 0 } }}>
      {hasData && (
        <Flex style={{ padding: 16 }}>
          <Input
            placeholder={t("pages.userInvites.searchPlaceholder")}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ maxWidth: 300 }}
            spellCheck={false}
          />
        </Flex>
      )}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredData}
        loading={isLoading}
        pagination={false}
        scroll={{ x: 800 }}
        locale={{ emptyText: emptyState }}
      />
    </Card>
  );
};
